// r47_calculator_explorer/src/editor.js
//
// CodeMirror 6 editor for the Program tab.  Bundled via esbuild into
// static/editor.bundle.js; see build/build-editor.sh.
//
// Exposes window.r47Editor with one method:
//   create(parent, initialText, onChange) → { getValue, setValue, focus, destroy }
//
// The API surface matches what programTab.js expects so the textarea
// fallback (if the bundle fails to load) is a drop-in replacement.
//
// A tiny R47 syntax mode provides highlighting for:
//   - Comments: #…, ;…, //…, @… (rejig ≥0.30 discards @ comments at parse)
//   - Alpha strings: 'text', "text", ‘text’, “text”
//   - Labels: LBL (followed by anything); local labels :name:
//   - Flow control: GTO, XEQ, RTN, END, .END.
//   - Tests/getters: any op ending in ? or # (SSIZE#, BATT#, …)
//   - Numbers: signed decimals with optional exponent; #B-style literals
//   - R47 op names: uppercase-ish tokens matching [A-Z][A-Z0-9._?!#+\-*/%=<>]*
//
// Comment rule (matches rejig doc/style.md): a comment must be separated
// from a preceding op/arg by whitespace, and '#' opens a comment only
// when standalone (followed by whitespace or EOL) — a trailing '#' is
// part of the op name (the 0.30.0 state getters), and '#B'-style based
// literals keep their leading '#'.
//
// Grammar is tolerant — anything it doesn't recognise is rendered as
// plain text so programs that use Unicode math symbols (𝑥⇄𝑦, √𝑥, etc.)
// render cleanly instead of being highlighted as "errors".

import { EditorState } from "@codemirror/state";
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  drawSelection,
  rectangularSelection,
  crosshairCursor,
} from "@codemirror/view";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands";
import {
  StreamLanguage,
  HighlightStyle,
  syntaxHighlighting,
  bracketMatching,
} from "@codemirror/language";
import { tags } from "@lezer/highlight";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { lintGutter, setDiagnostics } from "@codemirror/lint";

// ─── R47 stream parser ────────────────────────────────────────────────

const FLOW_TOKENS = new Set(["LBL", "GTO", "XEQ", "RTN", "END", ".END."]);

function r47StartState() {
  return { inString: false, stringQuote: "" };
}

function r47Token(stream, state) {
  // String continuation (multi-line strings are rare but possible).
  if (state.inString) {
    while (!stream.eol()) {
      const ch = stream.next();
      if (ch === state.stringQuote || ch === closingQuoteFor(state.stringQuote)) {
        state.inString = false;
        return "string";
      }
    }
    return "string";
  }

  if (stream.eatSpace()) return null;

  // Comments.  '#' opens a comment only when standalone (followed by
  // whitespace or EOL): 'SSIZE#' is an op name, '#B' is a based
  // literal, but '# note' and a bare trailing '#' are comments.
  // '@' is the RPL-style comment rejig 0.30.0 accepts in plain UTF-8
  // — and DISCARDS at parse, so it never reaches the calculator.
  if (stream.match(/^#(?=\s|$)/)) { stream.skipToEnd(); return "lineComment"; }
  if (stream.match(/^;.*/)) return "lineComment";
  if (stream.match(/^\/\/.*/)) return "lineComment";
  if (stream.match(/^@.*/)) return "lineComment";

  // Strings.  ASCII single/double quotes and their curly counterparts.
  const ch = stream.peek();
  if (ch === "'" || ch === '"' || ch === "\u2018" || ch === "\u201C") {
    const opener = stream.next();
    const closer = closingQuoteFor(opener);
    while (!stream.eol()) {
      const c = stream.next();
      if (c === closer) return "string";
    }
    state.inString = true;
    state.stringQuote = opener;
    return "string";
  }

  // Numbers (with optional sign, decimal, exponent, imaginary suffix).
  // The /u flag is REQUIRED: 𝐢 is an astral char, and without /u its
  // high surrogate becomes a mandatory literal — the rule then only
  // matches numbers that END in 𝐢, so plain numbers never highlight.
  if (stream.match(/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?𝐢?/u)) {
    return "number";
  }

  // '#B'-style based literals — a leading '#' directly attached to a
  // token is NOT a comment (rejig protects these from the REM read).
  if (stream.match(/^#\S+/)) {
    return "number";
  }

  // R47 local named labels (:name:) — rejig 0.30.0 / firmware
  // 04.00b0.  The colons are significant; render like a label name.
  if (stream.match(/^:[^\s:]+:/)) {
    return "string";
  }

  // .END. must be handled before the generic op name matcher so it
  // doesn't split on the dots.
  if (stream.match(/^\.END\./)) {
    return "controlKeyword";
  }

  // Op name.  Start with a letter or Unicode letter; body can include
  // alnum, dots, underscores, math symbols, arrows, ?/!/#/+/-/etc.
  // '#' in the body covers the 0.30.0 trailing-# state getters
  // (SSIZE#, BATT#, ADM#, etc.) -- without it the getter renders as
  // an op followed by a bogus comment and its argument greys out.
  if (stream.match(/^[A-Za-z_\u0080-\uffff][A-Za-z0-9._?!#+\-*/%<>=\u0080-\uffff]*/)) {
    const token = stream.current();
    // Flow-control keywords.
    if (FLOW_TOKENS.has(token)) return "controlKeyword";
    // Predicates end with "?"; state getters end with "#".
    if (token.endsWith("?") || token.endsWith("#")) return "atom";
    return "keyword";
  }

  stream.next();
  return null;
}

const r47Language = StreamLanguage.define({
  name: "r47",
  startState: r47StartState,
  token: r47Token,
});

function closingQuoteFor(opener) {
  if (opener === "'") return "'";
  if (opener === '"') return '"';
  if (opener === "\u2018") return "\u2019";
  if (opener === "\u201C") return "\u201D";
  return opener;
}

// ─── Highlight style ──────────────────────────────────────────────────

// Token colours are pulled from CSS variables on :root defined in
// calc.css (with dark-lcd overrides) so the editor stays readable on
// both light and dark LCD themes without needing a second theme
// definition here.
const r47Highlight = HighlightStyle.define([
  { tag: tags.lineComment,    color: "var(--r47-ed-comment, #7a7a80)", fontStyle: "italic" },
  { tag: tags.string,         color: "var(--r47-ed-string, #c27a2a)" },
  { tag: tags.number,         color: "var(--r47-ed-number, #3c6ea5)" },
  { tag: tags.keyword,        color: "var(--r47-ed-op, #2b5080)" },
  { tag: tags.controlKeyword, color: "var(--r47-ed-flow, #a0408a)", fontWeight: "600" },
  { tag: tags.atom,           color: "var(--r47-ed-test, #227a4a)" },
]);

// ─── Base theme matching the calc LCD palette ────────────────────────

const r47Theme = EditorView.theme({
  "&": {
    height: "100%",
    minHeight: "300px",
    // Follows the panel's text size (Settings → Text size): 14px at the
    // 15px default. Same rule on .program-editor-textarea in calc.css.
    fontSize: "calc(14em / 15)",
    lineHeight: "1.5",
    color: "var(--calc-lcd-fg, #303030)",
    background: "color-mix(in srgb, var(--calc-lcd-fg, #303030) 3%, var(--calc-lcd-bg, #e0e0e0))",
  },
  ".cm-content": {
    fontFamily: "var(--font-mono, ui-monospace, monospace)",
    padding: "8px 4px",
    caretColor: "var(--calc-f-gold, #e5ae5a)",
  },
  ".cm-gutters": {
    background: "color-mix(in srgb, var(--calc-lcd-fg, #303030) 6%, var(--calc-lcd-bg, #e0e0e0))",
    color: "color-mix(in srgb, var(--calc-lcd-fg, #303030) 45%, transparent)",
    borderRight: "1px solid color-mix(in srgb, var(--calc-lcd-fg, #303030) 12%, transparent)",
  },
  ".cm-activeLineGutter, .cm-activeLine": {
    backgroundColor: "color-mix(in srgb, var(--calc-f-gold, #e5ae5a) 10%, transparent)",
  },
  ".cm-cursor": {
    borderLeftColor: "var(--calc-f-gold, #e5ae5a)",
  },
  ".cm-selectionBackground, ::selection": {
    background: "color-mix(in srgb, var(--calc-f-gold, #e5ae5a) 25%, transparent) !important",
  },
  "&.cm-focused": {
    outline: "none",
  },
  "&.cm-focused > .cm-scroller": {
    outline: "none",
  },
}, { dark: false });

// ─── Public API ───────────────────────────────────────────────────────

function create(parent, initialText, onChange) {
  // Track whether the most recent setDiagnostics() came from Rejig.
  // The updateListener below auto-clears them when the user edits so
  // stale squiggles don't linger over text the user has since fixed.
  let _diagsPresent = false;

  const updateListener = EditorView.updateListener.of((update) => {
    if (!update.docChanged) return;
    if (typeof onChange === "function") {
      onChange(update.state.doc.toString());
    }
    if (_diagsPresent) {
      _diagsPresent = false;
      update.view.dispatch(setDiagnostics(update.state, []));
    }
  });

  const state = EditorState.create({
    doc: initialText || "",
    extensions: [
      lineNumbers(),
      lintGutter(),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      history(),
      drawSelection(),
      rectangularSelection(),
      crosshairCursor(),
      highlightSelectionMatches(),
      bracketMatching(),
      r47Language,
      syntaxHighlighting(r47Highlight),
      r47Theme,
      keymap.of([
        indentWithTab,
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap,
      ]),
      EditorView.lineWrapping,
      updateListener,
    ],
  });

  const view = new EditorView({ state, parent });

  // Convert a Rejig-style diagnostic (1-based line, message, level)
  // into the CM6 diagnostic shape (character offsets, severity).
  // Line out of range falls back to the last line of the document.
  function toCmDiag(d) {
    const doc = view.state.doc;
    let lineNum = d.line ?? d.fileLine ?? 0;
    if (typeof lineNum !== "number" || lineNum < 1) lineNum = 1;
    if (lineNum > doc.lines) lineNum = doc.lines;
    const line = doc.line(lineNum);
    const severity = d.level === "ERROR" || d.severity === "error" ? "error"
                   : d.level === "WARN"  || d.severity === "warning" ? "warning"
                   : "info";
    return {
      from: line.from,
      to: line.to,
      severity,
      message: d.message || d.raw || "(no message)",
      source: "rejig",
    };
  }

  return {
    getValue() { return view.state.doc.toString(); },
    setValue(text) {
      _diagsPresent = false;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: text || "" },
        effects: [], // setDiagnostics happens via the docChanged listener
      });
    },
    focus() { view.focus(); },
    destroy() { view.destroy(); },

    // Install a batch of Rejig diagnostics.  Safe to call repeatedly;
    // replaces the previous set.
    setDiagnostics(diags) {
      const list = (diags || []).map(toCmDiag);
      _diagsPresent = list.length > 0;
      view.dispatch(setDiagnostics(view.state, list));
    },
    clearDiagnostics() {
      _diagsPresent = false;
      view.dispatch(setDiagnostics(view.state, []));
    },

    // Scroll the editor so the given 1-based line is centered and
    // move the cursor there.  Used by the programTab footer to turn
    // diagnostic lines into clickable links.
    scrollToLine(lineNum) {
      const doc = view.state.doc;
      if (typeof lineNum !== "number" || lineNum < 1) lineNum = 1;
      if (lineNum > doc.lines) lineNum = doc.lines;
      const line = doc.line(lineNum);
      view.dispatch({
        selection: { anchor: line.from },
        effects: EditorView.scrollIntoView(line.from, { y: "center" }),
      });
      view.focus();
    },

    // Expose the raw view for advanced callers that want to attach
    // their own CM extensions or inspect state.
    _view: view,
  };
}

// _test exposes the raw tokenizer so the Node harness
// (tools/rejig/test_editor_tokenizer.js) can drive the PRODUCTION
// rule chain against the rejig CLI's lexing rules; inert in the browser.
window.r47Editor = { create, _test: { token: r47Token, startState: r47StartState } };
